import type {SignUpAcquisitionInput} from '@perfectpost/perfect-post-common';

/**
 * Premier contact (first touch) avec l'app : UTM, référent externe et page d'arrivée, enregistrés
 * UNE fois dans le stockage local du domaine — jamais écrasés par une visite suivante — puis
 * transmis à l'inscription (`POST /signup`, champ `acquisition`). Le serveur en déduit le canal
 * d'acquisition et le persiste sur l'utilisateur.
 *
 * Pas de cookie, rien de tiers. Les URL sont réduites à hôte + chemin : une query string peut porter
 * un e-mail ou un jeton (le lien d'inscription d'un parrainage porte l'adresse de l'invité).
 */

const STORAGE_KEY = 'pp:first-touch';
const UTM_KEYS = {
  source: 'utm_source',
  medium: 'utm_medium',
  campaign: 'utm_campaign',
  content: 'utm_content',
} as const;

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

const defaultStorage = (): Storage | undefined => {
  try {
    return window.localStorage;
  } catch {
    // Stockage bloqué (navigation privée stricte, cookies tiers coupés dans une iframe…).
    return undefined;
  }
};

/** Hôte + chemin d'un référent EXTERNE ; un référent de l'app elle-même n'est pas une provenance. */
const externalReferrer = (referrer: string, ownHostname: string): string | undefined => {
  if (referrer === '') {
    return undefined;
  }
  try {
    const url = new URL(referrer);
    if (url.hostname === ownHostname) {
      return undefined;
    }
    return `${url.protocol}//${url.hostname}${url.pathname}`;
  } catch {
    return undefined;
  }
};

export function captureFirstTouch(
  location: Pick<Location, 'search' | 'pathname' | 'hostname'> = window.location,
  referrer: string = document.referrer,
  storage: Storage | undefined = defaultStorage(),
  now: Date = new Date(),
): void {
  if (storage === undefined) {
    return;
  }
  try {
    if (storage.getItem(STORAGE_KEY) !== null) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const touch: SignUpAcquisitionInput = {landingPage: location.pathname, capturedAt: now.toISOString()};
    for (const [field, param] of Object.entries(UTM_KEYS) as Array<[keyof typeof UTM_KEYS, string]>) {
      const value = params.get(param);
      if (value !== null && value !== '') {
        touch[field] = value.slice(0, 200);
      }
    }
    const external = externalReferrer(referrer, location.hostname);
    if (external !== undefined) {
      touch.referrer = external;
    }
    storage.setItem(STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // Mesure best-effort : jamais d'erreur au démarrage de l'app pour elle.
  }
}

export function readFirstTouch(storage: Storage | undefined = defaultStorage()): SignUpAcquisitionInput | undefined {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as SignUpAcquisitionInput) : undefined;
  } catch {
    return undefined;
  }
}
